import { mutateOperations } from "@/api/operations/useOperationsQuery";
import { sites } from "@/api/sites/useSitesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { SitesAutoComplete } from "@/features/ui/autoComplete/site/sites-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { validateFormValues } from "@/utils/validation";
import styled from "@emotion/styled";
import { Box, Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { DefaultApiOperationsOperationIdPutRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export function OperationsUpdateForm(params: { formatterProps: any }) {
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const form = useForm({
    initialValues: {
      code: formatterProps.code,
      name: formatterProps.name,
      siteCode: formatterProps.siteCode,
      fromLocationCode: formatterProps.fromLocationCode,
      toLocationCode: formatterProps.toLocationCode,
    },
  });

  const { mutate: updateMutate } = useMutation(
    (params: DefaultApiOperationsOperationIdPutRequest) =>
      mutateOperations
        .update(params)
        .mutationFn(params as DefaultApiOperationsOperationIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["operations"]);
        closeModal(formatterProps);
        customAlert(
          `${t("message.editSuccess", { item: `${t("operation.operation")}` })}`,
          `${t("common.editSuccess")}`,
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("operation.code") }),
          `${t("common.editFail", { item: `${t("operation.operation")}` })}`,
          "red",
        );
      },
    },
  );

  const { data } = useQuery(sites.get({}));
  // const { data } = useWorkSitesQuery();

  const siteData = data?.data?.rows as any[];

  const siteCode =
    (siteData &&
      (siteData?.map((value) => {
        return value.code;
      }) as any)) ||
    [];

  const onSubmit = async () => {
    try {
      updateMutate({
        operationId: formatterProps.id as number,
        operationsGetRequest: {
          siteCode: form.values.siteCode as any,
          code: form.values.code as any,
          name: form.values.name as any,
          fromLocationCode: form.values.fromLocationCode as any,
          toLocationCode: form.values.toLocationCode as any,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("operation.code") }),
        `${t("common.editFail", { item: `${t("operation.operation")}` })}`,
        "red",
      );
    }
  };

  return (
    <Box maw={320} mx="auto">
      <Form>
        <ModalFieldSet>
          <TextInput
            withAsterisk
            label={t("operation.code")}
            placeholder={t("placeholder.input", { item: `${t("operation.code")}` })}
            defaultValue={formatterProps.code}
            {...form.getInputProps("code")}
            error={form.isDirty("code") ? t("common.toSave") : ""}
            styles={{
              input: {
                color: form.isDirty("code") ? "red !important" : "",
                borderColor: form.isDirty("code") ? "red !important" : "",
              },
              error: { fontSize: 11, color: "red" },
            }}
          />
          <TextInput
            withAsterisk
            label={t("operation.name")}
            placeholder={t("placeholder.input", { item: `${t("operation.name")}` })}
            defaultValue={formatterProps.name}
            {...form.getInputProps("name")}
            error={form.isDirty("name") ? t("common.toSave") : ""}
            styles={{
              input: {
                color: form.isDirty("name") ? "red !important" : "",
                borderColor: form.isDirty("name") ? "red !important" : "",
              },
              error: { fontSize: 11, color: "red" },
            }}
          />
          <SitesAutoComplete
            withAsterisk
            label={t("site.code")}
            width={"100%"}
            placeholder={t("placeholder.select", { item: `${t("site.code")}` })}
            data={siteCode}
            defaultValue={formatterProps.siteCode}
            {...form.getInputProps("siteCode")}
            error={form.isDirty("siteCode") ? t("common.toSave") : ""}
          />
          <LocationAutoComplete
            withAsterisk
            label={t("operation.fromLocationCode")}
            width={"100%"}
            placeholder={t("placeholder.select", { item: `${t("operation.fromLocationCode")}` })}
            defaultValue={formatterProps.fromLocationCode}
            {...form.getInputProps("fromLocationCode")}
            error={form.isDirty("fromLocationCode") ? t("common.toSave") : ""}
          />
          <LocationAutoComplete
            withAsterisk
            label={t("operation.toLocationCode")}
            width={"100%"}
            placeholder={t("placeholder.select", { item: `${t("operation.toLocationCode")}` })}
            defaultValue={formatterProps.toLocationCode}
            {...form.getInputProps("toLocationCode")}
            error={form.isDirty("toLocationCode") ? t("common.toSave") : ""}
          />

          <ModalConfirmBtnBox>
            <Button color="gray" onClick={closeModal}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={validateFormValues({
                pageType: "operations",
                values: form.values,
              })}
              onClick={onSubmit}
            >
              {t("common.edit", { item: `${t("operation.operation")}` })}
            </Button>
          </ModalConfirmBtnBox>
        </ModalFieldSet>
      </Form>
    </Box>
  );
}

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const ModalFieldSet = styled.fieldset`
  border: none;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalConfirmBtnBox = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
`;
